/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { buildDetachTargetsPayload } from '../../components/AssetTargetsDialog/helpers/assetProductLinks';
import { buildDetachTargets } from '../../components/details/view/ProductLinksSections/helpers/productLinkDetach';
import l10n from '../../shared/strings';
import { fetchVariantAssets } from './fetchVariantAssets';
import { resolveListingVariantFetchParamsByListingVariant } from './helpers/resolveListingVariantFetchParams';

const LS_KEY = 'picsio.hideDetachFromVariantWarning';
const strings = l10n.DETAILS.confirmDetachFromVariant;

export const detachProductVariantFromAssets = createAsyncThunk(
  'products/detachProductVariantFromAssets',
  async ({
    assetIds,
    productVariant,
    masterProductId,
  }, {
    dispatch,
    getState,
    extra: {
      sdk, Logger, utils, showErrorDialog, showDialogAsync,
    },
  }) => {
    try {
      if (!assetIds?.length || !productVariant?.variantId) {
        return { detached: false };
      }

      if (!utils.LocalStorage.get(LS_KEY)) {
        try {
          await showDialogAsync({
            title: strings.title,
            text: strings.text,
            checkbox: {
              label: strings.checkboxLabel,
              value: false,
              onChange: (value) => {
                utils.LocalStorage.set(LS_KEY, value);
              },
            },
            textBtnOk: strings.textBtnOk,
          }).promise;
        } catch (error) {
          /** User pressed "Cancel" */
          return { detached: false };
        }
      }

      const targets = buildDetachTargets(productVariant, masterProductId);

      await sdk.assets.detachFromTargets(
        assetIds,
        buildDetachTargetsPayload({ productId: masterProductId, targets }),
      );

      if (productVariant.type === 'listingVariant') {
        const productsState = getState().products;
        const params = resolveListingVariantFetchParamsByListingVariant(
          productsState,
          productVariant.productId,
          productVariant.variantId,
        );

        if (params) {
          await dispatch(fetchVariantAssets({
            productId: masterProductId,
            variantId: params.linkVariantId,
            channelId: params.channelId,
            masterVariantId: params.masterVariantId,
          }));
        }
      } else {
        await dispatch(fetchVariantAssets({
          variantId: productVariant.variantId,
          productId: masterProductId,
        }));
      }

      return {
        detached: true,
        assetIds,
        productVariant,
      };
    } catch (error) {
      const errorMessage = utils.getDataFromResponceError(error, 'msg') || 'Can not remove asset from product variant';
      showErrorDialog(errorMessage);
      Logger.error(new Error('Error detach product variant from assets'), { error }, [
        'DetachProductVariantFromAssetsFailed',
        { errorMessage: errorMessage || 'NoMessage' },
      ]);
      throw error;
    }
  },
);

export const reducer = (builder) => {
  builder
    .addCase(detachProductVariantFromAssets.pending, () => { /* noop */ })
    .addCase(detachProductVariantFromAssets.fulfilled, () => { /* noop */ })
    .addCase(detachProductVariantFromAssets.rejected, (state, { error }) => {
      state.error = error;
    });
};
